import React, { PropsWithChildren, useCallback, useContext, useMemo, useRef } from 'react';

import { CustomBottomSheetModalProvider } from '@/feature/alerts/components/CustomBottomSheetModalProvider';

import { EdgeComboModal, EdgeComboModalRef, EdgeComboProps } from './EdgeComboModal';

export type EdgeComboDialogContext = {
    showAcceptEdgeComboPairModal: (props: EdgeComboProps) => void;
};

const EdgeComboContext = React.createContext<EdgeComboDialogContext | undefined>(undefined);

/**
 * Provides access to the Modal used to display the 2 players that are in a edge combination
 */
export const EdgeComboModalProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const modalRef = useRef<EdgeComboModalRef>(null);

    const showAcceptEdgeComboPairModal = useCallback((props: EdgeComboProps) => {
        modalRef.current?.show(props);
    }, []);

    const value: EdgeComboDialogContext = useMemo(
        () => ({
            showAcceptEdgeComboPairModal,
        }),
        [showAcceptEdgeComboPairModal]
    );

    return (
        <CustomBottomSheetModalProvider>
            <EdgeComboContext.Provider value={value}>
                {children}
                <EdgeComboModal ref={modalRef} />
            </EdgeComboContext.Provider>
        </CustomBottomSheetModalProvider>
    );
};

export const useEdgeCombo = () => {
    const context = useContext(EdgeComboContext);
    if (context === undefined) {
        throw new Error('useEdgeCombo must be used within an EdgeComboModalProvider');
    }
    return context;
};
